import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  Inject,
  input,
  OnDestroy,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import {ReviewSettings} from '../../../types/ReviewSettings';
import {ReviewSummary} from '../../../types/ReviewSummary';
import {Review} from '../../../types/Review';
import {ReviewsEnum} from '../../../enums/PluralEnums';
import {CareproviderUnitService} from '../../../services/careprovider-unit.service';
import {PluralService} from '../../../services/plural.service';
import {ModalsControlService} from '../../../services/modals-control.service';
import {LoaderService} from '../../../services/loader.service';
import * as dayjs from 'dayjs';
import {ModalCommand} from '../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../enums/ModalEnums';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';
import {tap} from 'rxjs/operators';
import {ReviewsPage} from '../../../types/ReviewsPage';
import {ExternalReviews} from '../../../types/ExternalReviews';
import {ToolsService} from '../../../services/tools.service';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'app-ratings-list',
  templateUrl: './ratings-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RatingsListComponent implements OnDestroy {
  public reviewSettings = input<ReviewSettings>(null);
  public reviewSummary = input<ReviewSummary>(null);
  public reviews = input<Review[]>(null);
  public cpruId = input<number>(null);
  public name = input<string>(null);
  public externalReviews = input<ExternalReviews>(null);
  public noBorder = input<boolean>(false);
  public reviewsToDisplay = signal<Review[]>([]);
  public hasNextPage = signal<boolean>(true);
  public pluralReviews = computed<string>(() =>
    this.pluralService.getPluralString(ReviewsEnum, this.reviewSummary().reviews, false),
  );
  public overalRatingRounded = computed<number>(() => this.tools.round(this.reviewSummary().rating, 1));
  public isMoreVisible = signal<boolean[]>([]);
  private readonly itemsPerPage: number = 5;
  private readonly itemsPerFirstPage: number = 2;
  private isInitialLoad: boolean = true;
  private reviewsPageLoaded = signal<number>(1);

  constructor(
    private careproviderUnitService: CareproviderUnitService,
    private pluralService: PluralService,
    private modalControl: ModalsControlService,
    private loaderService: LoaderService,
    private tools: ToolsService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    dayjs.locale('sv');

    effect(
      () => {
        if (this.reviews() && this.reviewSummary()) {
          this.init();
        }
      },
      {allowSignalWrites: true},
    );
  }

  @HostBinding('class') get hostClasses(): string {
    return this.noBorder()
      ? 'block mb-8'
      : 'block md:rounded-lg md:border md:border-grey-9 md:bg-white md:px-4 md:py-5';
  }

  public ngOnDestroy() {
    this.reviewsToDisplay.set(null);
  }

  public onAfterRender(): void {
    if (this.reviewsToDisplay()) {
      this.reviewsToDisplay().forEach((item, index) => {
        this.isMoreVisible.update((prev) => {
          prev[index] = this.isElementClamped(index) || item.customerReviewComment !== null;
          return prev;
        });
      });
    }
  }

  public createRating() {
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.RATING,
      data: {
        id: this.cpruId(),
        name: this.name(),
        reviewSettings: this.reviewSettings(),
      },
    };
    this.modalControl.openModal(modalCommand);
  }

  public moreClick() {
    if (this.isInitialLoad) {
      const firstPage = this.reviews();
      if (this.reviewSummary().reviews <= this.itemsPerPage) {
        this.hasNextPage.set(false);
      }
      this.reviewsToDisplay.set(this.careproviderUnitService.prepareReviewsObject(firstPage));
      this.isInitialLoad = false;
    } else {
      this.loadNextItems();
    }
  }

  public reviewMoreClick(i: number): void {
    const commentElement = this.document.getElementById(`reviewComment-${i}`);
    if (commentElement) {
      commentElement.classList.remove('line-clamp-3');
    }

    const replyElement = this.document.getElementById(`reviewReply-${i}`);
    if (replyElement) {
      replyElement.classList.remove('hidden');
    }

    const moreElement = this.document.getElementById(`reviewMore-${i}`);
    if (moreElement) {
      moreElement.remove();
    }
  }

  private init() {
    this.reviewsToDisplay.set([]);
    let firstPage = this.reviews();
    if (this.itemsPerFirstPage < this.itemsPerPage) {
      this.isInitialLoad = true;
    }
    if (this.reviewSummary().reviews <= (this.isInitialLoad ? this.itemsPerFirstPage : this.itemsPerPage)) {
      this.hasNextPage.set(false);
    } else if (this.isInitialLoad) {
      firstPage = firstPage.slice(0, this.itemsPerFirstPage);
      this.hasNextPage.set(true);
    }
    this.reviewsToDisplay.update((prev) => [...prev, ...this.careproviderUnitService.prepareReviewsObject(firstPage)]);
  }

  private isElementClamped(i: number): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const commentElement = this.document.getElementById(`reviewComment-${i}`);
      if (commentElement) {
        return commentElement.scrollHeight > commentElement.clientHeight;
      }
    }
    return false;
  }

  private loadNextItems(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    const page = this.reviewsPageLoaded() + 1;
    this.careproviderUnitService
      .getCareproviderUnitReviews(this.cpruId(), page, this.itemsPerPage)
      .pipe(
        tap(() => this.loaderService.removeFromQueue(queryId)),
        tap((res) => this.processReviewsPage(res)),
      )
      .subscribe();
  }

  private processReviewsPage(loadedPage: ReviewsPage): void {
    this.reviewsPageLoaded.set(loadedPage.page);
    this.hasNextPage.set(loadedPage.hasNextPage);
    this.reviewsToDisplay.update((prev) => [
      ...prev,
      ...this.careproviderUnitService.prepareReviewsObject(loadedPage.items),
    ]);
  }
}
